/**
 * msi-corp_app-ready.js
 * ---------------------------------------------------------------------------
 * 通用「第三方 App 何時真的跑完」偵測核心 + 全站註冊中心。
 *
 * 【為什麼需要這層】
 * 商城會掛上多個第三方 App（發票、金流、訂閱、客服…），它們幾乎都有同一個問題：
 * 沒有公開「我初始化完成了」的事件，卻又會在初始化期間改動頁面（鎖按鈕、換 DOM）。
 * 本模組把「判斷某個 App 就緒」抽象成可組合的**訊號 (signal)**，
 * 讓每個 App 只需要宣告用哪些訊號，不必各寫一套偵測程式。
 *
 * 【硬性契約：fail-open】
 * `detectAppReady()` 永遠會 resolve，永不 reject、永不無限等待。
 * 偵測失敗時以 `reason: 'timeout'` 放行 —— 第三方 App 掛掉不可以連帶癱瘓商城。
 *
 * 【對外介面】
 *   await window.MSI.apps.ready('ako');                    // 隨時可 await，不怕錯過時機
 *   document.addEventListener('msi:app-ready', (e) => e.detail);        // 任一 App 就緒
 *   document.addEventListener('msi:app-ready:ako', (e) => e.detail);    // 指定 App 就緒
 *
 * 【GDPR】不讀寫 cookie / storage，不蒐集也不傳送任何個人資料。
 * ---------------------------------------------------------------------------
 */

export const READY_EVENT = 'msi:app-ready';
const GLOBAL_KEY = 'MSI';

/* ========================================================================= *
 * 就緒訊號 (signals)
 *
 * 每個訊號是 `{ name, reason, start(ctx) => cleanup }`：
 *   - name    ：診斷用的唯一識別字串
 *   - reason  ：命中時寫進結果的短代碼，供呼叫端做分支
 *   - start   ：開始偵測，命中時呼叫 `ctx.settle(detail)`；回傳清理函式
 * 新增偵測方式＝新增一個 factory，不需要改動 detectAppReady。
 * ========================================================================= */

/**
 * 網路活動靜默訊號（預設也是最泛用的一種）。
 *
 * 刻意不去找 App 的 DOM 節點或全域變數 —— 那些都是未公開的實作細節，
 * 對方一改版就會失效。改用瀏覽器層級的客觀訊號：該網域（含 script、CSS、
 * 字型、以及它自己的 fetch/XHR）的資源活動靜默一段時間，即代表初始化流程結束。
 *
 * 注意：若從頭到尾沒偵測到任何該網域的活動，本訊號**不會**命中
 * （代表 App 根本沒載入或被封鎖），此時交由 maxWaitMs 保險絲放行。
 *
 * @param {object} options
 * @param {string} options.pattern         網址片段，例如 `akocommerce.com`
 * @param {number} [options.quietPeriodMs] 靜默多久視為就緒
 */
export function networkIdleSignal({ pattern, quietPeriodMs = 1200 }) {
  return {
    name: `network-idle(${pattern})`,
    reason: 'network-idle',
    start({ settle, logger }) {
      if (typeof PerformanceObserver !== 'function') {
        logger.warn('此瀏覽器不支援 PerformanceObserver，network-idle 訊號停用（改由逾時放行）');
        return () => {};
      }

      let hits = 0;
      let quietTimer = null;

      const handleEntries = (entries) => {
        for (const entry of entries) {
          if (!entry.name.includes(pattern)) continue;

          hits += 1;
          if (hits === 1) logger.phase('detect:first-activity', { pattern, url: entry.name });
          else logger.debug(`網路活動 #${hits}`, { url: entry.name, duration: Math.round(entry.duration) });

          // 每次活動都重新計算靜默期。
          clearTimeout(quietTimer);
          quietTimer = setTimeout(() => settle({ hits }), quietPeriodMs);
        }
      };

      const observer = new PerformanceObserver((list) => handleEntries(list.getEntries()));
      try {
        // buffered 補上本模組載入前就已完成的資源。
        observer.observe({ type: 'resource', buffered: true });
      } catch {
        observer.observe({ entryTypes: ['resource'] });
        handleEntries(performance.getEntriesByType('resource'));
      }

      return () => {
        clearTimeout(quietTimer);
        observer.disconnect();
      };
    },
  };
}

/** 這些鍵一旦被當成路徑片段查找，會走到原型鏈而非物件自身屬性。 */
const UNSAFE_PATH_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

/**
 * 讀取 `a.b.c` 形式的全域路徑；任一層不存在即回傳 undefined。
 * 防禦性地擋掉 `__proto__` / `prototype` / `constructor`——
 * 目前 `path` 只來自 Liquid 設定標籤，並非使用者輸入，但保留此檢查可避免
 * 日後若改由較不受信任的來源（例如 CMS 欄位）組出路徑時，意外走到原型鏈。
 */
function resolveGlobalPath(path) {
  return path.split('.').reduce((scope, key) => {
    if (scope == null || UNSAFE_PATH_KEYS.has(key)) return undefined;
    return scope[key];
  }, window);
}

/**
 * 全域變數出現訊號：適用於有公開全域物件（例如 `window.Foo.initialized`）的 App。
 * @param {object} options
 * @param {string} options.path           全域路徑，例如 `Foo.instance`
 * @param {number} [options.pollMs]       輪詢間距
 */
export function globalSignal({ path, pollMs = 100 }) {
  return {
    name: `global(${path})`,
    reason: 'global',
    start({ settle }) {
      if (resolveGlobalPath(path) !== undefined) {
        settle({ path });
        return () => {};
      }
      const timer = setInterval(() => {
        if (resolveGlobalPath(path) === undefined) return;
        clearInterval(timer);
        settle({ path });
      }, pollMs);
      return () => clearInterval(timer);
    },
  };
}

/**
 * 元素出現訊號：適用於會插入自家 UI 節點的 App。
 * 註：綁定第三方 DOM 結構較脆弱，僅在沒有更穩定訊號時使用。
 * @param {object} options
 * @param {string} options.selector
 */
export function elementSignal({ selector }) {
  return {
    name: `element(${selector})`,
    reason: 'element',
    start({ settle }) {
      if (document.querySelector(selector)) {
        settle({ selector });
        return () => {};
      }
      const observer = new MutationObserver(() => {
        if (!document.querySelector(selector)) return;
        observer.disconnect();
        settle({ selector });
      });
      observer.observe(document.documentElement, { subtree: true, childList: true });
      return () => observer.disconnect();
    },
  };
}

/**
 * 自訂事件訊號：適用於有公開 ready 事件的 App（最理想的情況）。
 * @param {object} options
 * @param {string} options.type
 * @param {EventTarget} [options.target]
 */
export function eventSignal({ type, target = document }) {
  return {
    name: `event(${type})`,
    reason: 'event',
    start({ settle }) {
      const handler = (event) => settle({ type, detail: event.detail });
      target.addEventListener(type, handler, { once: true });
      return () => target.removeEventListener(type, handler);
    },
  };
}

/* ========================================================================= *
 * 偵測器
 * ========================================================================= */

/**
 * 等待某個 App 就緒。**無論結果如何都一定會 resolve。**
 *
 * @param {object} options
 * @param {string} options.appId
 * @param {Array<ReturnType<typeof networkIdleSignal>>} options.signals
 * @param {'any'|'all'} [options.mode]  any＝任一訊號命中即就緒；all＝全部命中才算
 * @param {number} [options.maxWaitMs]  fail-open 保險絲
 * @param {ReturnType<import('./msi-corp_debug-logger.js').createLogger>} logger
 * @returns {Promise<{appId:string, reason:string, signal:string|null, elapsedMs:number, detail:*}>}
 */
export function detectAppReady({ appId, signals, mode = 'any', maxWaitMs = 8000 }, logger) {
  return new Promise((resolve) => {
    const startedAt = performance.now();
    const pending = new Set(signals.map((signal) => signal.name));
    const fired = [];
    const cleanups = [];
    let settled = false;
    let hardTimer = null;

    const finish = (reason, signalName, detail) => {
      if (settled) return;
      settled = true;
      clearTimeout(hardTimer);
      for (const cleanup of cleanups) {
        try {
          cleanup();
        } catch (error) {
          logger.warn('訊號清理失敗', error);
        }
      }

      const result = {
        appId,
        reason,
        signal: signalName,
        elapsedMs: Math.round(performance.now() - startedAt),
        detail,
        fired: fired.slice(),
      };
      logger.phase('detect:settled', result);
      if (reason === 'timeout') {
        logger.warn(
          fired.length === 0
            ? '逾時且完全沒偵測到任何就緒訊號 —— App 可能沒載入或被封鎖，已由保險絲放行'
            : 'App 在時限內未完全就緒，已由保險絲強制放行',
          { maxWaitMs, fired },
        );
      }
      resolve(result);
    };

    if (signals.length === 0) {
      logger.warn('沒有設定任何就緒訊號，立即視為就緒');
      finish('no-signals', null, undefined);
      return;
    }

    // fail-open 保險絲：不管偵測結果如何，時間到就放行。
    hardTimer = setTimeout(() => finish('timeout', null, { fired }), maxWaitMs);

    logger.phase('detect:start', { mode, maxWaitMs, signals: signals.map((s) => s.name) });

    for (const signal of signals) {
      const settle = (detail) => {
        if (settled || !pending.has(signal.name)) return;
        pending.delete(signal.name);
        fired.push(signal.name);
        logger.phase('detect:signal', { signal: signal.name, detail });
        if (mode === 'all' && pending.size > 0) return;
        finish(signal.reason, signal.name, detail);
      };

      try {
        cleanups.push(signal.start({ settle, logger, appId }) || (() => {}));
      } catch (error) {
        logger.error(`訊號啟動失敗：${signal.name}`, error);
        pending.delete(signal.name);
      }
    }
  });
}

/* ========================================================================= *
 * 註冊中心
 * ========================================================================= */

/** @type {Map<string, object>} 已註冊的 App */
const registry = new Map();
/** @type {Map<string, {promise: Promise<*>, resolve: (value:*) => void}>} 尚未註冊就先被 await 的 App */
const waiters = new Map();

/**
 * 取得某個 App 的就緒 Promise。
 * **可以在該 App 註冊之前呼叫** —— 回傳的 Promise 會在註冊並就緒後才 resolve，
 * 呼叫端因此不需要在意載入順序（不怕太早問、也不怕錯過事件）。
 * @param {string} appId
 */
export function whenReady(appId) {
  const record = registry.get(appId);
  if (record) return record.ready;

  let waiter = waiters.get(appId);
  if (!waiter) {
    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });
    waiter = { promise, resolve };
    waiters.set(appId, waiter);
  }
  return waiter.promise;
}

/**
 * 註冊一個 App，並在其就緒時廣播事件。
 * @param {object} record
 * @param {string} record.id
 * @param {Promise<*>} record.ready
 * @param {object} [record.guard]
 * @param {object} [record.logger]
 * @param {object} [record.config]
 */
export function registerApp(record) {
  if (registry.has(record.id)) {
    record.logger?.warn(`App「${record.id}」重複註冊，後者已忽略`);
    return registry.get(record.id);
  }
  registry.set(record.id, record);

  record.ready.then((result) => {
    const detail = { ...result, appId: record.id };
    document.dispatchEvent(new CustomEvent(READY_EVENT, { detail }));
    document.dispatchEvent(new CustomEvent(`${READY_EVENT}:${record.id}`, { detail }));

    const waiter = waiters.get(record.id);
    if (waiter) {
      waiter.resolve(result);
      waiters.delete(record.id);
    }
    record.logger?.phase('ready:dispatched', detail);
  });

  return record;
}

/** 全站唯一的對外命名空間，重複載入時沿用既有物件。 */
export const apps = (() => {
  const existing = window[GLOBAL_KEY];
  if (existing?.apps) return existing.apps;

  const api = Object.freeze({
    register: registerApp,
    ready: whenReady,
    get: (appId) => registry.get(appId) || null,
    list: () => Array.from(registry.keys()),
    /** 一次取回所有 App 的診斷快照，線上出事時請使用者跑這個。 */
    diagnostics: () =>
      Object.fromEntries(
        Array.from(registry, ([id, record]) => [
          id,
          { config: record.config, guard: record.guard?.diagnostics ?? null },
        ]),
      ),
  });

  window[GLOBAL_KEY] = Object.freeze({ ...(existing || {}), apps: api });
  return api;
})();
